import type { SetPasswordRequest } from '@painpal/contracts';
import { useState, type FormEvent } from 'react';
import { useSetPassword } from '../../api/mutations';
import { useAccount } from '../../api/queries';
import { ApiRequestError, errorMessage } from '../../lib/api';
import styles from './settings.module.css';

export const SET_PASSWORD_ID = 'set-password';
export const PASSWORD_MISMATCH = 'The two passwords don’t match.';
export const PASSWORD_EXISTS_MESSAGE = 'You already have a password. Sign in with your email and password.';
export const EMAIL_TAKEN_MESSAGE = 'That email is already used by another PainPal account.';

const CODE_MESSAGES: Record<string, string> = { 'password-exists': PASSWORD_EXISTS_MESSAGE, 'email-taken': EMAIL_TAKEN_MESSAGE };

export function setPasswordMessage(error: Error): string {
  return (error instanceof ApiRequestError ? CODE_MESSAGES[error.code] : undefined) ?? errorMessage(error);
}

interface PasswordFormProps { busy: boolean; error: string | null; onSubmit: (request: SetPasswordRequest) => void }

function PasswordForm({ busy, error, onSubmit }: PasswordFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  function edit(set: (value: string) => void, value: string): void {
    set(value);
    setMismatch(false);
  }
  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (password !== confirm) return setMismatch(true);
    setMismatch(false);
    onSubmit({ email: email.trim().toLowerCase(), newPassword: password });
  }
  const message = mismatch ? PASSWORD_MISMATCH : error;
  return (
    <form className={styles.passwordForm} onSubmit={submit} aria-label="Set a password">
      <label className={styles.formField}>
        <span>EMAIL</span>
        <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
      </label>
      <label className={styles.formField}>
        <span>NEW PASSWORD</span>
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => edit(setPassword, e.currentTarget.value)} />
      </label>
      <label className={styles.formField}>
        <span>CONFIRM PASSWORD</span>
        <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => edit(setConfirm, e.currentTarget.value)} />
      </label>
      {message !== null && <p role="alert" className={`alert-hot ${styles.formError}`}>{message}</p>}
      <button type="submit" className="btn btn-ink" disabled={busy}>SET PASSWORD</button>
    </form>
  );
}

export function SetPasswordCard() {
  const account = useAccount();
  const save = useSetPassword();
  if (save.isSuccess) {
    return (
      <section id={SET_PASSWORD_ID} className={styles.card} data-tone="strong" aria-labelledby="set-password-title">
        <h2 id="set-password-title" className={styles.cardTitle}>PASSWORD SET</h2>
        <p className={styles.note}>Sign in with {save.variables.email} and your password from now on, with or without Strava.</p>
      </section>
    );
  }
  if (account.data?.hasPassword !== false) return null;
  return (
    <section id={SET_PASSWORD_ID} className={styles.card} data-tone="strong" aria-labelledby="set-password-title">
      <h2 id="set-password-title" className={styles.cardTitle}>SET A PASSWORD</h2>
      <p className={styles.note}>You sign in with Strava. Add your email and a password so you can also sign in without it.</p>
      <PasswordForm
        busy={save.isPending}
        error={save.error === null ? null : setPasswordMessage(save.error)}
        onSubmit={save.mutate}
      />
    </section>
  );
}
